import {
  BackstageTypography,
  PageTheme,
  createBaseThemeOptions,
  createUnifiedTheme,
  genPageTheme,
  palettes,
  shapes,
} from '@backstage/theme';
import { buildComponents } from './componentOverrides';
import {
  ModeTokens,
  brand,
  dark,
  fonts,
  light,
  navigation,
  status,
} from './tokens';

/**
 * Sparrow LaunchPad unified themes.
 *
 * Design direction: "Sparrow Teal" — brand teal anchored on the logo color
 * (#00828D), warm amber accents, dark navigation chrome in both modes,
 * bundled Inter typography and soft, layered surfaces.
 */

const typography: BackstageTypography = {
  htmlFontSize: 16,
  fontFamily: fonts.sans,
  h1: { fontSize: 30, fontWeight: 700, marginBottom: 8 },
  h2: { fontSize: 26, fontWeight: 700, marginBottom: 8 },
  h3: { fontSize: 22, fontWeight: 600, marginBottom: 6 },
  h4: { fontSize: 18, fontWeight: 600, marginBottom: 6 },
  h5: { fontSize: 16, fontWeight: 600, marginBottom: 4 },
  h6: { fontSize: 14, fontWeight: 600, marginBottom: 2 },
};

/** Teal-family page header gradients, one hue per page type. */
const buildPageThemes = (): Record<string, PageTheme> => {
  const gen = (colors: string[]) =>
    genPageTheme({ colors, shape: shapes.wave });

  return {
    home: gen([brand.teal800, brand.teal600]),
    documentation: gen([brand.teal900, brand.teal700]),
    tool: gen(['#0B3B46', brand.teal600]),
    service: gen([brand.teal800, '#0E7C86']),
    website: gen(['#065666', brand.teal600]),
    library: gen(['#044A52', '#0891B2']),
    other: gen([brand.teal900, brand.teal800]),
    app: gen(['#00404D', brand.teal600]),
    apis: gen([brand.teal700, '#0E9AA3']),
  };
};

const buildNavigationPalette = (mode: 'light' | 'dark') => {
  const nav = navigation[mode];
  return {
    background: nav.background,
    indicator: nav.indicator,
    color: nav.color,
    selectedColor: nav.selectedColor,
    navItem: {
      hoverBackground: nav.hoverBackground,
    },
    submenu: {
      background: nav.submenuBackground,
    },
  };
};

const buildTheme = (mode: 'light' | 'dark', t: ModeTokens) => {
  const base = mode === 'light' ? palettes.light : palettes.dark;

  return createUnifiedTheme({
    ...createBaseThemeOptions({
      palette: {
        ...base,
        primary: {
          main: t.primary,
          dark: mode === 'light' ? brand.teal800 : brand.teal500,
          light: mode === 'light' ? brand.teal500 : brand.teal300,
          contrastText: t.primaryContrast,
        },
        secondary: {
          main: brand.amber500,
          dark: '#B45309',
          light: brand.amber400,
          contrastText: '#1F1300',
        },
        background: {
          default: t.canvas,
          paper: t.surface,
        },
        text: {
          primary: t.text,
          secondary: t.textMuted,
          disabled: t.textSubtle,
          hint: t.textSubtle,
        },
        divider: t.divider,
        border: t.border,
        textSubtle: t.textMuted,
        textVerySubtle: t.textSubtle,
        link: mode === 'light' ? brand.teal700 : brand.teal300,
        linkHover: mode === 'light' ? brand.teal600 : brand.teal400,
        highlight: mode === 'light' ? '#FEF3E2' : 'rgba(251, 191, 36, 0.12)',
        status: {
          ...base.status,
          ok: status[mode].ok,
          warning: status[mode].warning,
          error: status[mode].error,
          running: status[mode].running,
          pending: status[mode].pending,
          aborted: status[mode].aborted,
        },
        navigation: buildNavigationPalette(mode),
      },
      fontFamily: fonts.sans,
      typography,
    }),
    components: buildComponents(mode, t),
    pageTheme: buildPageThemes(),
    defaultPageTheme: 'home',
  });
};

/** Light theme — default. */
export const launchpadLightTheme = buildTheme('light', light);

/** Dark theme. */
export const launchpadDarkTheme = buildTheme('dark', dark);
